import { useEffect, useRef, useState } from "react";

/**
 * useCameraPreview — manages getUserMedia for the go-live camera preview.
 * Returns `{videoRef, facing, flipCamera}`. Auto-starts when `open` flips true
 * and stops all tracks when it flips false / on unmount.
 */
export default function useCameraPreview(open, onPermission) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("user");

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async (mode) => {
    try {
      if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
      stop();
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.play().catch(() => {});
      }
      onPermission?.(true, null);
    } catch (err) {
      // Camera permission or hardware issue; user still sees gradient fallback.
      onPermission?.(false, err?.name || "UnknownError");
    }
  };

  useEffect(() => {
    if (open) startCamera(facing);
    else stop();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const flipCamera = () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    startCamera(next);
  };

  return { videoRef, facing, flipCamera };
}
